import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Stack, Typography} from 'views/components';
import {Translate} from 'views/module';

import PlaybookPill from './components/PlaybookPill';

const PlaybookView = (props) => {
  const {
    clientBackend,
    clientTasks,
    onViewAllPlaybookTasks,
    playbooks,
  } = props;

  const [deletingPlaybookRn, setDeletingPlaybookRn] = React.useState(null);

  const activePlaybooks = React.useMemo(() => {
    const playbookMap = new Map(playbooks.map((playbook) => [playbook.playbookRn, playbook]));
    const activePlaybookGroups = new Map();
    const openPlaybookTasks = clientTasks.filter((task) =>
      /^RN:PLAYBOOK-V1-[^:]+$/.test(task.playbookRn) && task.status === 'OPEN',
    );

    openPlaybookTasks.forEach((task) => {
      const currentGroup = activePlaybookGroups.get(task.playbookRn) || [];

      currentGroup.push(task);
      activePlaybookGroups.set(task.playbookRn, currentGroup);
    });

    return Array.from(activePlaybookGroups.entries())
        .map(([playbookRn, tasks]) => {
          const playbook = playbookMap.get(playbookRn);
          const executionCount = new Set(tasks.map((task) => task.playbookExecutionRn)).size;

          return {
            playbookRn,
            title: playbook?.title || '-',
            executionCount,
          };
        })
        .sort((firstPlaybook, secondPlaybook) => firstPlaybook.title.localeCompare(secondPlaybook.title));
  }, [clientTasks, playbooks]);

  const handleDeletePlaybook = async (playbookRn) => {
    if (deletingPlaybookRn === playbookRn) {
      return;
    }

    try {
      setDeletingPlaybookRn(playbookRn);
      await clientBackend.playbooks.delete(playbookRn);
    } finally {
      setDeletingPlaybookRn(null);
    }
  };

  return (
    <Stack spacing={2}>
      <Typography
        variant='subtitle2'
        sx={{
          fontWeight: 700,
          fontSize: '11px',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: '#A8A49E',
        }}
      >
        <Translate expression='client.ClientDetail.Playbooks.Title' />
        {activePlaybooks.length > 0 ? ` (${activePlaybooks.length})` : ''}
      </Typography>
      {
        activePlaybooks.length === 0 ?
          <Typography
            variant='body2'
            sx={{
              color: '#6B6760',
              fontSize: '12px',
            }}
          >
            <Translate expression='client.ClientDetail.Playbooks.Empty' />
          </Typography> :
          <Stack
            direction='row'
            gap={1}
            sx={{
              flexWrap: 'wrap',
            }}
          >
            {
              activePlaybooks.map((playbook) => (
                <PlaybookPill
                  key={playbook.playbookRn}
                  playbook={playbook}
                  deletingPlaybookRn={deletingPlaybookRn}
                  onDelete={handleDeletePlaybook}
                  onViewAllPlaybookTasks={onViewAllPlaybookTasks}
                />
              ))
            }
          </Stack>
      }
    </Stack>
  );
};

PlaybookView.propTypes = {
  clientBackend: PropTypes.object.isRequired,
  clientTasks: PropTypes.array,
  onViewAllPlaybookTasks: PropTypes.func.isRequired,
  playbooks: PropTypes.array,
};

const mapStateToProps = (state) => ({
  playbooks: state.playbooks.allPlaybooks,
});

export default connect(mapStateToProps, dispatch)(React.memo(PlaybookView));
